import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {map} from 'rxjs/operators';
import {forkJoin, Subject, Subscription} from 'rxjs';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ChargeSchedulesService,
    ChargeScheduleInterface,
    ChargeTypeModel,
    ChargeGroupModel,
    ChargeScheduleModel,
    ChargeScheduleSearchModel,
    ChargeScheduleAssignAccountInputModel,
    ChargeScheduleUpdateAccountInputModel,
    ChargeScheduleAssignAccountResponseInterface,
    AccountChargeScheduleModel
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {Helper, SelectControl} from '../../../shared/classes';
import {SelectOptionInterface} from '../../../shared/components/select';

import {FeesFormErrorStatesEnum} from '../enums/error-states';

import {
    FeeSelector,
    FeeTypeSelector,
    FeeGroupSelector,
    FeesArraySelector,
    FeesFormControlsInterface,
    FeeStartDate,
    FeeEndDate, BulkFeesFormControls
} from '../../../../assets/descriptors';
import * as moment from 'moment';
import {environment} from "appEnv/environment";

export class BulkFeesForm {
    private chargeSchedulesService: ChargeSchedulesService;

    private readonly fees: { [feeTypeCode: string]: ChargeScheduleModel[] };
    private selectedFees: ChargeScheduleModel[] | undefined;
    private isValidityUpdate = false;
    public updateModel: AccountChargeScheduleModel | ChargeScheduleModel;

    public form: FormGroup;
    public controls: FeesFormControlsInterface[];
    public feesArraySelector: string;
    public isValidityPerAccount: boolean | undefined;

    public getFeesFailedNotifier: Subject<boolean>;

    public getFeesSubscriptions: Subscription[];
    public feeTypeControlValueChangesSubscriptions: Subscription[];
    public feeControlChangeSubscription: Subscription | undefined;

    constructor(chargeSchedulesService: ChargeSchedulesService) {
        this.chargeSchedulesService = chargeSchedulesService;

        this.fees = {};
        this.getFeesFailedNotifier = new Subject();
        this.feesArraySelector = FeesArraySelector;
        this.getFeesSubscriptions = this.feeTypeControlValueChangesSubscriptions = [];
        this.isValidityPerAccount = false;
    }

    private fixFeeControlOptions(feeTypeCode: string, feeFormControl: FormControl, feeControl: SelectControl): void {
        feeControl.resetValue();
        feeControl.setValidators([Validators.required]);

        feeControl.options.splice(0, feeControl.options.length);

        feeFormControl.setValidators(feeControl.validators);
        feeFormControl.setValue(feeControl.value, { emitEvent: false });

        if (Object.keys(this.fees).indexOf(feeTypeCode) < 0) {
            const search = new ChargeScheduleSearchModel(feeTypeCode);

            this.getFeesSubscriptions.push(this.chargeSchedulesService.searchSchedules(search)
                .subscribe((feesResponse: ServerResponseData<ChargeScheduleInterface[]>) => {
                    if ((feesResponse.status !== HttpStatusCodesEnum.Ok) ||
                        Helper.errorsHelper().checkForInvalidRespondedArray<ChargeScheduleInterface>(feesResponse)) {
                        this.getFeesFailedNotifier.next(true);
                    }
                    else {
                        const allFees = Helper.collectionFactory<ChargeScheduleInterface, ChargeScheduleModel>(
                            ChargeScheduleModel,
                            feesResponse.data as ChargeScheduleInterface[]
                        );

                        const fees = allFees.filter(fee => fee.getValidityPerAccount())

                        if (fees.length === 0) {
                            this.getFeesFailedNotifier.next(true);
                        }
                        else {
                            this.fees[feeTypeCode] = fees;
                            this.fixFeeControlOptions(feeTypeCode, feeFormControl, feeControl);
                        }
                    }
                }));
        } else {
            const feeCodes: string[] = [];

            const feeControlOptions = this.fees[feeTypeCode].filter((fee: ChargeScheduleModel) => {
                return fee.isActive;
            }).map((fee: ChargeScheduleModel) => {
                feeCodes.push(fee.chargeScheduleRefId);

                return {
                    label: fee.chargeScheduleName,
                    value: fee.chargeScheduleRefId
                };
            });

            feeControl.options.push(...feeControlOptions);

            feeControl.extendValidators(InArrayValidator(feeCodes));

            feeFormControl.setValidators(feeControl.validators);
        }
        this.selectedFees = this.fees[feeTypeCode];

        if (this.isValidityUpdate && this.selectedFees) {
            feeFormControl.setValue( this.updateModel.chargeScheduleRefId);
        }

        this.form.updateValueAndValidity({ emitEvent: false });
    }

    private assignFee(
        formGroup: FormGroup,
        formGroupIndex: number,
        accountReferenceId: string
    ): ServerResponse<ChargeScheduleModel> {
        let feeName = '';

        const feeReferenceId = formGroup.value[FeeSelector];
        const feeType = formGroup.value[FeeTypeSelector];
        const startDate = formGroup.value[FeeStartDate];
        const endDate = formGroup.value[FeeEndDate] ?? null;

        if (typeof this.controls[formGroupIndex] !== 'undefined') {
            const filteredFeeSelectOptions = this.controls[formGroupIndex].fee.options
                .filter((option: SelectOptionInterface) => option.value === feeReferenceId);

            if (filteredFeeSelectOptions.length > 0) {
                feeName = filteredFeeSelectOptions[0].label;
            }
        }
        if (this.isValidityUpdate) {

            return this.createRequestForUpdate(feeName, feeReferenceId, accountReferenceId, startDate, endDate);
        }

        return this.createRequestForAssignment(feeName, feeType, feeReferenceId, accountReferenceId, startDate, endDate);
    }
    private createRequestForUpdate(
        feeName: string,
        feeReferenceId: string,
        accountReferenceId: string,
        startDate: string,
        endDate: string): ServerResponse<ChargeScheduleModel>{
        const oldStartDate = moment(this.updateModel.startDate).format(environment.dateFormatLocalDate);
        const oldEndDate = this.updateModel.endDate
            ? moment(this.updateModel.endDate).format(environment.dateFormatLocalDate)
            : null;
        const model = new ChargeScheduleUpdateAccountInputModel(this.updateModel.referenceId, feeReferenceId,
            accountReferenceId, oldStartDate, endDate,
            oldStartDate, oldEndDate);

        return this.chargeSchedulesService.updateAccountChargeFeeSchedule(model).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse(
                        response,
                        FeesFormErrorStatesEnum.FeeUpdateFailed,
                        feeName
                    );
                }

                return Helper.createSuccessResponse<ChargeScheduleModel>(null);
            }));
    }

    private createRequestForAssignment(
        feeName: string,
        feeType: string,
        feeReferenceId: string,
        accountReferenceId: string,
        startDate: string,
        endDate: string): ServerResponse<ChargeScheduleModel> {
        const model = new ChargeScheduleAssignAccountInputModel(feeReferenceId, accountReferenceId, startDate, endDate);
        return this.chargeSchedulesService.assignAccount(model).pipe(
            map((response: ServerResponseData<ChargeScheduleAssignAccountResponseInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse(
                        response,
                        FeesFormErrorStatesEnum.AssignFeeFailed,
                        feeName
                    );
                }

                let assignedFee: ChargeScheduleModel = null;

                const data = response.data as ChargeScheduleAssignAccountResponseInterface;
                const feesByType = this.fees[feeType];

                if (feesByType && (feesByType.length > 0)) {
                    const filteredFees = feesByType.filter((fee: ChargeScheduleModel) => {
                        return fee.chargeScheduleRefId === feeReferenceId;
                    });

                    if (filteredFees.length > 0) {
                        assignedFee = filteredFees[0];
                        assignedFee.startDate = startDate ?? null;
                        assignedFee.endDate = endDate ?? null;
                        assignedFee.referenceId = data.reference_id;
                    }
                }

                return Helper.createSuccessResponse<ChargeScheduleModel>(assignedFee);
            })
        );
    }

    private applyTimePeriodValidations(): void {
        const formGroupsArray = this.form.get(this.feesArraySelector) as FormArray;
        const startDateControl = formGroupsArray.controls[0].get(FeeStartDate);
        const endDateControl = formGroupsArray.controls[0].get(FeeEndDate);
        startDateControl.reset();
        startDateControl.setValidators(null);
        endDateControl.reset();
        endDateControl.setValidators(null);

        if (this.isValidityPerAccount && this.isValidityPerAccount === true) {
            if (this.isValidityUpdate) {
                endDateControl.setValidators(Validators.required);
            }
            else {
                startDateControl.setValidators(Validators.required);
            }
        } else {
            startDateControl.setErrors(null);
            endDateControl.setErrors(null);
        }

        if (this.isValidityUpdate) {
            startDateControl.setValue(this.updateModel.startDate);
            endDateControl.setValue(this.updateModel.endDate);
        }

        formGroupsArray.updateValueAndValidity();
    }

    initForm(allFeeGroups: ChargeGroupModel[], isValidityUpdate: boolean, updateModel: ChargeScheduleModel): void {
        this.form = new FormGroup({});
        this.controls = [BulkFeesFormControls(isValidityUpdate)];

        const typeCodes: string[] = [];

        const formArray = new FormArray([]);

        let updateChargeTypeCode: string;

        if (isValidityUpdate) {
            this.isValidityUpdate = isValidityUpdate;
            this.updateModel = updateModel;
            updateChargeTypeCode = updateModel.chargeTypeCode;
        }

        this.controls.forEach((controlsItem: FeesFormControlsInterface, controlsItemIndex: number) => {
            const feeCategoryControl = new FormControl(controlsItem.feeGroup.value, controlsItem.feeGroup.validators);
            const feeTypeControl = new FormControl(controlsItem.feeType.value, controlsItem.feeType.validators);
            const feeControl = new FormControl(controlsItem.fee.value, controlsItem.fee.validators);
            const startDate = new FormControl(controlsItem.startDate.value, controlsItem.startDate.validators);
            const endDate = new FormControl(controlsItem.endDate.value, controlsItem.endDate.validators);

            (controlsItem.feeGroup as SelectControl).options = allFeeGroups.map(feeCategory => {
                return {
                    label: feeCategory.name,
                    value: feeCategory.name
                }
            })

            this.feeTypeControlValueChangesSubscriptions.push(feeCategoryControl.valueChanges.subscribe((category: string) => {
                const feeCategory = allFeeGroups.find(f => f.name === category);

                controlsItem.feeType.options = feeCategory.chargeTypes.map((type: ChargeTypeModel) => {
                    typeCodes.push(type.code);

                    return {
                        label: type.name,
                        value: type.code
                    };
                });

                this.controls[0].feeType.extendValidators(InArrayValidator(typeCodes));

                this.isValidityPerAccount = false;
            }));

            this.feeTypeControlValueChangesSubscriptions.push(feeTypeControl.valueChanges.subscribe((type: string) => {
                this.fixFeeControlOptions(type, feeControl, controlsItem.fee);
                this.isValidityPerAccount = false;
            }));

            this.feeControlChangeSubscription = feeControl.valueChanges.subscribe((fee: string) => {
                const selectedFeeModel = this.selectedFees.find( (chargeSchedule: ChargeScheduleModel) => {
                    return chargeSchedule.chargeScheduleRefId === fee;
                });

                if (selectedFeeModel) {
                    this.isValidityPerAccount = selectedFeeModel.getValidityPerAccount();
                    this.applyTimePeriodValidations();
                }
            });

            const formGroup = new FormGroup({
                [FeeGroupSelector]: feeCategoryControl,
                [FeeTypeSelector]: feeTypeControl,
                [FeeSelector]: feeControl,
                [FeeStartDate]: startDate,
                [FeeEndDate]: endDate
            });

            if (isValidityUpdate) {
                feeTypeControl.setValue(updateChargeTypeCode);
            }
            formArray.insert(controlsItemIndex, formGroup);
        });

        this.form.addControl(FeesArraySelector, formArray);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const formGroupsArray = this.form.get(this.feesArraySelector) as FormArray;
        const startDate = formGroupsArray.controls[0].get(FeeStartDate);
        const endDate = formGroupsArray.controls[0].get(FeeEndDate);

        if (this.isValidityPerAccount === true && startDate && endDate) {
            if (this.isValidityUpdate && moment(endDate.value).unix() < moment(startDate.value).unix()) {
                endDate.setErrors({ WrongEndDateValue: true });
            }

            if (moment(endDate.value).unix() === moment(startDate.value).unix()) {
                endDate.setErrors({ EqualDataValue: true });
            }
        }
        if (this.form.invalid) {
            if (formGroupsArray) {
                formGroupsArray.controls.forEach((formGroup: FormGroup, formGroupIndex: number) => {
                    if (typeof this.controls[formGroupIndex] !== 'undefined') {
                        Object.keys(this.controls[formGroupIndex]).forEach((controlKey: string) => {
                            if (typeof this.controls[formGroupIndex][controlKey] !== 'undefined') {
                                const control = this.controls[formGroupIndex][controlKey];
                                const formControl = formGroup.get(control.name);

                                if (formControl && formControl.invalid) {
                                    control.errors = Object.keys(formControl.errors)
                                        .map((errorKey: string) => messageFunction(control.placeholder)[errorKey]);
                                }
                            }
                        });
                    }
                });
            }

            return false;
        }

        return true;
    }

    getSelectedFeeReferenceId(): string {
        return this.form.value[this.feesArraySelector][0][FeeSelector];
    }

    createRequest(
        accountReferenceId: string,
        feeSchedule: ChargeScheduleModel = null,
        createRequestForUnAssignment = false
    ): ServerResponse<ChargeScheduleModel[]> {
        if (!createRequestForUnAssignment || this.isValidityUpdate) {
            const formGroups = (this.form.get(this.feesArraySelector) as FormArray).controls;

            const requests = formGroups.map((formGroup: FormGroup, formGroupIndex: number) => {
                return this.assignFee(formGroup, formGroupIndex, accountReferenceId);
            });

            if (requests.length > 0) {
                return forkJoin(requests).pipe(
                    map((responses: ServerResponseData<ChargeScheduleModel>[]) => {
                        if (Helper.errorsHelper().checkForErrorResponses<ChargeScheduleModel>(responses)) {
                            return Helper.errorsHelper().createReducedErrorResponse<ChargeScheduleModel>(responses);
                        }

                        const fees = responses.map((response: ServerResponseData<ChargeScheduleModel>) => {
                            return response.data as ChargeScheduleModel;
                        }).filter((fee: ChargeScheduleModel) => (fee !== null));

                        return Helper.createSuccessResponse<ChargeScheduleModel[]>(fees);
                    })
                );
            }
        }

        if (typeof feeSchedule.chargeScheduleRefId !== 'undefined') {
            const model = new ChargeScheduleAssignAccountInputModel(feeSchedule.chargeScheduleRefId,
                accountReferenceId, feeSchedule.startDate, feeSchedule.endDate, feeSchedule.referenceId);

            return this.chargeSchedulesService.unAssignAccount(model).pipe(
                map((response: ServerResponseData<null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            FeesFormErrorStatesEnum.UnassignFeeFailed
                        );
                    }

                    return Helper.createSuccessResponse<ChargeScheduleModel[]>([]);
                })
            );
        }

        return Helper.createSuccessResponseObservable<ChargeScheduleModel[]>([]);
    }
}
